'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  ShieldCheck,
  Phone,
  Clock,
  FileText,
  ChevronRight,
  CheckCircle,
  Scale,
} from 'lucide-react';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function TermsConditionsPage() {
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            LEGAL COMPLIANCE
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            Terms &{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Conditions
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            Please read these terms carefully. By accessing our travel portal or making bookings,
            you agree to our policies.
          </p>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main terms body (Left) */}
          <div className="lg:col-span-8 text-left space-y-8 animate-fadeInUp">
            {/* Acceptance */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <Scale className="w-5.5 h-5.5 text-accent" />
                Agreement and Acceptance of Terms
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The travel portal and services are offered to you on your acceptance of the Terms &
                Conditions, and Notices as mentioned here, without going for any kind of
                modification. By accessing the website Faremaps.com or Faremaps, or executing the
                flight bookings, you agree and are bound to the Terms & Conditions and Policy
                Statements. If you do not agree to the Terms of Use, we strongly recommend that you
                do not access the travel portal or make travel bookings.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The pronouns such as “We”, “Us” define Faremaps (Cheapo Wings, Inc) and the
                Faremaps.com, while the use of pronouns like “You”, “Customer” and “Traveler” define
                the traveler/user who is visiting our website or executing flight booking. We
                reserve the absolute right to amend or alter the Terms of Use and/or the Privacy
                Policy without serving the prior notice to the Customers/Travelers.
              </p>

              <div className="p-5 bg-slate-50 border border-slate-200/60 rounded-2xl space-y-3">
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest">
                  By using our services, you confirm that:
                </h4>
                <ul className="space-y-2.5">
                  {[
                    'You are at least 18 Years or above;',
                    'You possess the legitimate authority to execute the travel services and/or build a legal obligation;',
                    'You will use the travel portal in absolute compliance with the Terms of Use;',
                    'You will make the legitimate travel booking for yourself or for another traveler for whom you are legally authorized to act;',
                    'The information provided by you on Faremaps.com is correct, accurate, current, and complete.',
                  ].map((req, i) => (
                    <li
                      key={i}
                      className="flex gap-2.5 items-start text-slate-500 text-xs font-semibold"
                    >
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Flight Bookings */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                Direct & Indirect Booking of Flight Tickets
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The bookings of Flight Tickets made with Faremaps.com and/or Faremaps are processed
                either directly by the Airline or through our Travel Suppliers. There are different
                airlines that are covered under flight booking, and some flight ticket bookings are
                done on the airlines that have non-refundable policies.
              </p>
            </div>

            {/* Financial Transactions */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                US & International Flight Bookings (Fees & Currency)
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The flight tickets that you book on Faremaps/Faremaps.com may be non-refundable.
                Under such a scenario, the tickets cannot be cancelled or amended under any
                circumstances. However, if the airline permits, the ticket may be cancelled, but the
                customer/traveler has to pay cancellation charges along with the fee applicable to
                the processing travel agency.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                Besides, the refund, if any, is processed back into the credit card or payment
                source within 7-10 Business Days. The payment for flight booking will be made in the
                USD. When the booking of the flight is processed using a Credit Card outside the
                boundaries of the USA, there will be conversion charges applied.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The charges on this conversion as appearing on the Statement of Credit Card of the
                Respective Bank will be in the local currency, and this is likely to variate from
                the Currency of Purchase Amount as appearing on the Billing Summary of the Travel
                Reservation. Besides, there is also a Foreign Transaction Fee charged by your Credit
                Card Issuing Bank, if it has the presence exterior to the boundaries of the United
                States.
              </p>

              <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl text-[11px] font-bold text-blue-800 leading-normal">
                🚨 <strong>Important Financial Notice:</strong> ALL TRAVEL BOOKINGS MADE THROUGH
                Faremaps.com ARE AND WILL BE ESSENTIALLY THE INTERNATIONAL FINANCIAL TRANSACTIONS BY
                YOUR BANK OR THE CREDIT CARD COMPANY FOR THE REASON THAT Faremaps.COM OR Faremaps IS
                GOING TO TRANSFER THE PAYMENTS TO THE INTERNATIONAL TRAVEL SUPPLIER, WHO MAY BE
                OUTSIDE THE BOUNDARIES OF THE USA OR NORTH AMERICA.
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                Booking Notifications & Confirmations
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                It is pertinent that the Currency Exchange Rate shall be decided by the bank issuing
                you the credit card, or the credit card company for that matter. Furthermore, the
                International Transaction Fee will also be charged on the Day of Processing the
                International Transaction. Make sure that you establish contact with your respective
                bank or the respective Credit Card Company in case you have queries related to the
                Exchange Rates and International Transaction Fee.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                After you have made the purchases of travel services from us, you will receive an
                Email, titling “Booking Notification”. There is the scenario where the travel
                booking will have a “Booking Confirmation Number” before the issuance of the ticket.
                Under this condition, the process of booking is incomplete, and the fare is
                changeable unless the ticket has not been actuated.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                After the actuation of the ticket, an eTicket will be issued and it will be received
                in your valid Email.
              </p>

              <div className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl text-[11px] font-semibold text-slate-500 leading-normal">
                💡 <strong>Pre-flight Confirmation:</strong> It is important and relevant too that
                you make the Confirmation of your Flight Ticket with the respective Airline at least
                24 Hours before the Departure of the Domestic Flights and at least 72 Hours before
                the International Flights Departure.
              </div>
            </div>

            {/* Travel Supplier Cancelations */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                Cancellations and Amendments Implemented by Airlines
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                It is entirely with the airlines or the supplier to make the changes with respect to
                the flight timings and/or annul the flights altogether. Such
                alterations/cancelations in the flights may be the result of operational barriers,
                or other reasons. Although, Faremaps or Faremaps.com is not accountable for any of
                these alterations/cancelations by the respective supplier, we will make sincere
                efforts on our behalf to assist the customer/traveler in all such scenarios.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                If the flight is canceled by the international airline or the respective tour
                operator, the regulations and rights of customer/traveler are within the scope that
                prevails within the Terms of Use of the Suppliers or the Airlines. You will be
                authorized to choose any of the following options:
              </p>

              <ul className="space-y-3.5">
                {[
                  'Choose another flight on the same airline without paying the additional costs;',
                  'Make changes in your destination routes by booking the flight with another carrier without going for additional cost;',
                  'Avail the future credit note or the voucher as the rightful means for the Customer/Traveler.',
                ].map((choice, i) => (
                  <li
                    key={i}
                    className="flex gap-2.5 items-start text-slate-600 text-xs font-semibold"
                  >
                    <span className="w-5 h-5 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-accent text-[10px] font-black flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span>{choice}</span>
                  </li>
                ))}
              </ul>

              <p className="text-slate-500 text-xs font-bold pt-3 border-t border-slate-100">
                Rescheduling Note: When the changes in your flight itinerary are expedited before
                starting your journey, it is not mandated to have the flight tickets issued afresh,
                or undergo a revalidation. However, in this situation, Faremaps.com and the Faremaps
                shall not take any accountability of rescheduling of the flights across the flying
                route.
              </p>
            </div>

            {/* Validity */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                Rationality and Legitimacy of Terms
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                The Rationale and Legitimacy of Terms and Conditions are fulfilled in all
                circumstances, albeit, if these are found illegitimate in any Court of Law or holds
                the Subjudice or Unenforceable or Null and Void in any Appellate Authority, then
                those stated terms shall be stroked-off, and will not hold any validity, Now and for
                the Future. However, the remaining Terms and Conditions shall remain valid and
                completely enforceable in all circumstances.
              </p>
            </div>
          </div>

          {/* Sidebar widget (Right) */}
          <div className="lg:col-span-4 sticky top-[110px] space-y-6">
            {/* VIP Desk Card */}
            {showPhoneNumber && (
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm text-left space-y-6">
                <div>
                  <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                    Legal Queries Desk
                  </h4>
                  <p className="text-slate-500 text-xs font-semibold leading-relaxed mb-4">
                    For inquiries about our terms of booking and airline legal disclosures, talk to
                    our support.
                  </p>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3.5 bg-slate-950 hover:bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-premium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-glow">
                      <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-20"></span>
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        TOLL-FREE 24/7
                      </div>
                      <div className="text-sm font-black text-white mt-1 tracking-tight leading-none group-hover:text-accent-light transition-colors">
                        {phoneNumber}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                  Verify Bookings
                </h4>
                <div className="flex items-start gap-3 bg-slate-50 border border-slate-200/60 p-4 rounded-2xl">
                  <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-slate-800 text-xs font-bold leading-tight">
                      100% Secure Agency
                    </h5>
                    <p className="text-slate-400 text-[10px] font-semibold mt-1 leading-normal">
                      Validated transactions via industry-standard merchant systems.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
